import { Slot } from '@radix-ui/react-slot';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Spinner } from './Spinner.js';

/**
 * Button.
 *
 * Every height and pad resolves from the `--control-*` tokens, which is what
 * keeps a button, an input and a select trigger the same height when they sit
 * on the same toolbar row — and what makes the compact density switch work
 * without this file knowing density exists.
 *
 * ── VARIANTS ──────────────────────────────────────────────────────────────
 *
 *   primary    the one thing this screen is for. Near-black, not brand blue:
 *              in a UI already carrying blue links and blue info states, a
 *              neutral fill is the only thing that reliably reads as THE action
 *   secondary  everything else that needs a boundary
 *   ghost      chrome — icon buttons, row menus, things that should recede
 *              until reached for
 *   danger     destructive and irreversible. Deliberately scarce; in this app
 *              almost nothing is destructive, because the ledger is append-only
 *
 * There is no "link" variant. A thing that navigates is an anchor, and giving
 * it button styling is how you end up with buttons that cannot be opened in a
 * new tab. Use `asChild` with an <a> instead.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-action-primary text-action-primary-fg',
    'hover:bg-action-primary-hover active:bg-action-primary-active',
  ),
  secondary: cn(
    'bg-action-secondary text-action-secondary-fg border border-action-secondary-border',
    'hover:bg-action-secondary-hover active:bg-action-secondary-active',
  ),
  ghost: cn(
    'text-action-ghost-fg',
    'hover:bg-action-ghost-hover hover:text-primary active:bg-action-ghost-active',
  ),
  danger: cn(
    'bg-action-danger text-action-danger-fg',
    'hover:bg-action-danger-hover active:bg-action-danger-active',
  ),
};

const SIZE: Record<ButtonSize, string> = {
  xs: 'h-(--control-height-xs) px-(--control-padding-x-xs) text-xs gap-1',
  sm: 'h-(--control-height-sm) px-(--control-padding-x-sm) text-sm gap-1.5',
  md: 'h-(--control-height-md) px-(--control-padding-x-md) text-md gap-2',
  lg: 'h-(--control-height-lg) px-(--control-padding-x-lg) text-lg gap-2',
};

/** Square when there is no label, so an icon button is not a stretched pill. */
const ICON_ONLY: Record<ButtonSize, string> = {
  xs: 'w-(--control-height-xs) px-0',
  sm: 'w-(--control-height-sm) px-0',
  md: 'w-(--control-height-md) px-0',
  lg: 'w-(--control-height-lg) px-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders before the label. Pass an icon component, not a string. */
  leading?: ReactNode;
  /** Renders after the label — chevrons for menus, arrows for navigation. */
  trailing?: ReactNode;
  /**
   * Shows a spinner and blocks interaction, WITHOUT collapsing the label.
   * The button keeps its width, so a toolbar does not reflow mid-submit and
   * move the next control under the user's cursor.
   */
  loading?: boolean;
  /**
   * Required when there is no visible label. A bare icon button is invisible
   * to a screen reader otherwise, and this is the single most common
   * accessibility defect in dashboards.
   */
  'aria-label'?: string;
  /**
   * Render as the child element instead of a <button>, keeping all styling.
   * Use for links: `<Button asChild><a href="…">Open</a></Button>`.
   */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    leading,
    trailing,
    loading = false,
    disabled,
    asChild = false,
    className,
    children,
    type,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const iconOnly = children == null || children === '';

  return (
    <Comp
      ref={ref}
      // Buttons inside a <form> default to type="submit", which submits the
      // form when someone clicks "Add line". Defaulting to "button" makes
      // submission something you opt into rather than something you forget.
      // `asChild` cases get no type, since the child may not be a button.
      {...(asChild ? {} : { type: type ?? 'button' })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center whitespace-nowrap',
        'rounded-(--control-radius) font-medium',
        'transition-colors duration-(--m-fast) ease-out',
        'press-motion',
        // A disabled control must not look interactive OR respond to hover.
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        iconOnly && ICON_ONLY[size],
        className,
      )}
      {...rest}
    >
      {/* The label stays laid out and simply turns invisible, so the button
          keeps its exact width while loading. Replacing children with a
          spinner makes toolbars jump on every submit. */}
      <span className={cn('contents', loading && 'invisible')}>
        {leading}
        {children}
        {trailing}
      </span>
      {loading && (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size={size === 'lg' ? 16 : 14} />
        </span>
      )}
    </Comp>
  );
});

